const formatEur = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

const Row = ({ label, value, strong, muted }) => (
  <div className={`flex items-baseline justify-between gap-4 ${strong ? "font-semibold text-slate-900" : muted ? "text-slate-500" : "text-slate-700"}`}>
    <span className="truncate">{label}</span>
    <span className="whitespace-nowrap tabular-nums">{value}</span>
  </div>
);

/** Cellule « Reste dû » de la liste des factures (vue bureau).
 *  Montant seul + un point ambre si un avoir en brouillon existe. Tout le détail
 *  — total, avoirs imputés nommés, règlements, acomptes déduits, avoirs en attente —
 *  vit dans une infobulle unique au survol. Aucune ligne de détail sous le montant. */
export const ResteDuCell = ({ doc }) => {
  const reste = doc.remaining ?? ((doc.total_ttc || 0) - (doc.total_paid || 0));
  const solde = reste <= 0.009;
  const imputed = doc.avoir_imputed_details || [];
  const drafts = doc.avoir_draft_details || [];
  const totalPaid = doc.total_paid || 0;
  const acomptes = doc.subtype === "solde" ? (doc.acompte_deductions || []) : [];

  const hasActivity =
    totalPaid > 0 || imputed.length > 0 || drafts.length > 0 ||
    (doc.avoir_refund_count || 0) > 0 || acomptes.length > 0;

  if (!hasActivity) return <span className="text-sm text-slate-300">-</span>;

  const hasTooltip = imputed.length > 0 || drafts.length > 0 || totalPaid > 0 || acomptes.length > 0;

  return (
    <div className="relative inline-flex flex-col items-end group/reste" data-testid={`reste-cell-${doc.id}`}>
      <div className="flex items-center justify-end gap-1">
        <span
          className={`text-[12px] font-medium ${solde ? "text-emerald-600" : "text-amber-600"}`}
          data-testid={`remaining-${doc.id}`}
        >
          {formatEur(reste)}
        </span>
        {drafts.length > 0 && (
          <span
            className="h-1.5 w-1.5 rounded-full bg-amber-500"
            aria-label="Avoir en brouillon"
            data-testid={`avoir-draft-dot-${doc.id}`}
          />
        )}
      </div>

      {hasTooltip && (
        <div
          role="tooltip"
          data-testid={`reste-tooltip-${doc.id}`}
          className="pointer-events-none absolute right-0 top-full z-40 mt-1 hidden w-64 rounded-md border border-slate-200 bg-white p-2.5 text-left text-[11px] leading-relaxed shadow-xl group-hover/reste:block"
        >
          <Row label="Total" value={formatEur(doc.total_ttc || 0)} />
          {acomptes.length > 0 && (
            <div className="mt-1">
              <div className="text-slate-500">Acomptes déduits</div>
              {acomptes.map((a, i) => (
                <Row
                  key={i}
                  muted
                  label={a.invoice_number ? `Facture ${a.invoice_number}` : "Acompte"}
                  value={`− ${formatEur(a.amount_ttc)}`}
                />
              ))}
            </div>
          )}
          {imputed.map((a, i) => (
            <Row key={i} label={`Avoir ${a.number}`} value={`− ${formatEur(a.ttc)}`} />
          ))}
          {totalPaid > 0 && <Row label="Règlements" value={`− ${formatEur(totalPaid)}`} />}
          <div className="my-1 border-t border-slate-100" />
          <Row strong label="Reste dû" value={formatEur(reste)} />
          {drafts.length > 0 && (
            <div className="mt-1.5 rounded bg-amber-50 px-1.5 py-1 text-amber-700" data-testid={`reste-tooltip-draft-${doc.id}`}>
              En attente :{" "}
              {drafts
                .map((a) => `avoir ${a.number} de ${formatEur(a.ttc)}`)
                .join(", ")}
              , non émis — n'impute rien pour l'instant
            </div>
          )}
        </div>
      )}
    </div>
  );
};
